import React from "react"
import { observer } from "mobx-react-lite"
import {
  Text,
  createStyles,
  Flex,
  Stack,
  Button,
  Group,
  TypographyStylesProvider,
  Avatar,
  Tooltip,
} from "@mantine/core"
import { type CourseDto, getEventStatus } from "../../common"
import { Image } from "../../shared/ui/Image"
import { API_URL } from "../../shared/config"
import { fDate } from "../../shared/lib/dates"
import { Link } from "react-router-dom"
import { PATHS } from "../../shared/lib/paths"

const useStyles = createStyles((theme) => ({
  card: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 12,
    boxShadow: "rgba(0, 0, 0, 0.09) 0px 3px 12px",
    flexGrow: 1,
  },

  cardTop: {
    margin: 4,
    backgroundColor: "rgb(248,244,241)",
    position: "relative",
    overflow: "hidden",
    borderRadius: 12,
    zIndex: 0,

    "&::before": {
      content: "''",
      width: "100%",
      height: "100%",
      backgroundImage: "url(/assets/funky-lines.webp)",
      position: "absolute",
      top: 0,
      left: 0,
      zIndex: -1,
      opacity: 0.15,
    },
  },

  cardBottom: {
    flexGrow: 1,
    padding: "20px 24px 24px",
  },

  title: {
    whiteSpace: "pre-wrap",
  },

  desc: {
    color: theme.colors.gray[6],
  },

  tags: {
    position: "absolute",
    top: 12,
    left: 12,
    zIndex: 1,
  },

  badge: {
    width: "fit-content",
    fontSize: 14,
    fontWeight: 600,
    backgroundColor: theme.colors.gray[1],
    color: theme.black,
    padding: "4px 8px",
    borderRadius: 6,
  },

  highlight: {
    position: "relative",
    background: theme.colors.blue[0],
    borderRadius: theme.radius.sm,
    color: theme.colors.blue[6],
    fontWeight: 700,
    padding: "4px 10px",
  },

  avatarGroup: {
    position: "absolute",
    right: 32,
    top: 300,
    transform: "translateY(-50%)",
  },
}))

type Props = {
  course: CourseDto
}

export const CourseCard = observer(function CourseCard(props: Props) {
    const { classes } = useStyles()
    const { course } = props

    const { description } = course.meta
    const { startDate, duration } = course.landing
    const { cover, name } = course

    const team = course.landing.sections.team
    const visibleTeam = team.slice(0, 3)
    const invisibleTeamNames = team.length > 3 ? team.slice(3).map(person => person.name) : undefined

    return (
      <Flex direction="column" className={classes.card}>
        <Group spacing={8} className={classes.tags}>
          <div className={classes.badge}>{`Продолжительность: ${duration}`}</div>
        </Group>
        <Flex className={classes.cardTop} h={300} align="center" justify="center">
          <Image
            w={220}
            objectFit="contain"
            sx={{ borderRadius: 20 }}
            src={`${API_URL}/public${cover}`}
          />
        </Flex>
        <Stack spacing={12} className={classes.cardBottom}>

          <Group className={classes.avatarGroup} position="right">
            <Tooltip.Group openDelay={230} closeDelay={100}>
              <Avatar.Group spacing="xs">
                {visibleTeam.map(person => (
                  <Tooltip key={person._id} label={person.name} withArrow>
                    <Avatar radius="50%" size={48} src={`${API_URL}/media/${person.avatar}`}/>
                  </Tooltip>
                ))}
                {invisibleTeamNames !== undefined && (
                  <Tooltip
                    withArrow
                    label={
                      <>
                        {invisibleTeamNames.map((name, index) => (
                          <div key={index}>{name}</div>
                        ))}
                      </>
                    }
                  >
                    <Avatar color="blue.6" radius="50%" size={48}>{`+${invisibleTeamNames.length}`}</Avatar>
                  </Tooltip>
                )}
              </Avatar.Group>
            </Tooltip.Group>
          </Group>

          <Text className={classes.title} fw={700} fz={24}>{name}</Text>
          <TypographyStylesProvider fz={14}>
            <div dangerouslySetInnerHTML={{ __html: description }}/>
          </TypographyStylesProvider>

          <Stack mt="auto" spacing={4}>
            <Text>
              Дата старта потока&nbsp;-&nbsp;
              <Text span className={classes.highlight}>
                {fDate(startDate, "D MMMM")}
              </Text>
            </Text>
            <Text fz={14} c="dimmed">{getEventStatus(startDate)}</Text>
          </Stack>

          <Button
            component={Link}
            to={PATHS.TRAINING.COURSES.DETAILS(course._id, course.slug)}
            radius={10}
            mt={6}
            fullWidth
            color="dark"
          >
            Узнать подробнее
          </Button>
        </Stack>
      </Flex>
    )
  },
)
